import { useEffect, useState } from "react";
import { useAvailability } from "../../contexts/AvailabilityContext";
import { ResourceDWR } from "../../dto/ResourceDWR";
import { useLocationConfiguration } from "../LocationConfiguration";
import { ResourceView } from "../../dto/ResourceView";

export function useOnShelfError(
  resource: ResourceDWR | ResourceView,
  pickupBranchId: string | null
) {
  const { getAvailabilityForHoldings } = useAvailability();
  const { locationConfiguration } = useLocationConfiguration();
  const { allowOnShelfHolds } =
    locationConfiguration?.pacSettings?.userActionSettings || {};

  const [onShelfError, setOnShelfError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const check = async () => {
      if (!pickupBranchId || allowOnShelfHolds) {
        isMounted && setOnShelfError(false);
        return;
      }

      const onShelfHoldings = resource.holdingsInformations.filter(
        (h) => h.branchIdentifier === pickupBranchId
      );

      if (onShelfHoldings.length === 0) {
        isMounted && setOnShelfError(false);
        return;
      }

      const onShelfAvailability = await getAvailabilityForHoldings(
        resource.id,
        onShelfHoldings
      );

      const circulating = onShelfAvailability.filter(
        ({ nonCirculating }) => !nonCirculating
      );

      if (!resource.serial) {
        isMounted &&
          setOnShelfError(circulating.some(({ available }) => available));
        return;
      }

      // serial handling
      const everyAvailable = circulating.every(({ available }) => available);

      // if some are not available on shelf allow
      if (!everyAvailable) {
        isMounted && setOnShelfError(false);
        return;
      }

      // need to ensure we have some circulating items at other locations
      const holdingsAtOtherLocations = resource.holdingsInformations.filter(
        (h) => h.branchIdentifier !== pickupBranchId
      );

      if (holdingsAtOtherLocations.length === 0) {
        isMounted && setOnShelfError(true);
        return;
      }

      const availabilityAtOtherLocations = await getAvailabilityForHoldings(
        resource.id,
        holdingsAtOtherLocations
      );

      isMounted &&
        setOnShelfError(
          availabilityAtOtherLocations.every(
            ({ nonCirculating }) => nonCirculating
          )
        );
    };

    if (resource?.id) {
      check();
    }

    return () => {
      isMounted = false;
    };
  }, [resource?.id, pickupBranchId, allowOnShelfHolds]);

  return onShelfError;
}
